import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Sparkle, Truck, ShieldCheck, WhatsappLogo, Star, EnvelopeSimple, CaretLeft, CaretRight } from '@phosphor-icons/react';
import ProductCard from '../components/ProductCard';
import FAQSection from '../components/FAQSection';
import RecentlyViewed from '../components/RecentlyViewed';
import { getItems, getCategories, getCarouselSlides } from '../utils/api';
import { toast } from 'sonner';

const Home = () => {
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [carouselSlides, setCarouselSlides] = useState([]);

  // Default hero slides (fallback when no carousel in DB)
  const defaultHeroSlides = [
    {
      background: 'https://images.unsplash.com/photo-1558882268-15aa056d885f?w=1920',
      title: 'Elegance',
      subtitle: 'Redefined',
      description: 'Discover our exquisite collection of premium imitation jewelry. Each piece tells a story of timeless beauty and sophisticated craftsmanship.',
      cta: 'Shop Now',
      link: '/shop'
    },
    {
      background: 'https://images.unsplash.com/photo-1515562141207-7a88fb7ce338?w=1920',
      title: 'Shine',
      subtitle: 'Brighter',
      description: 'Elevate your style with our stunning gold-plated jewelry. Perfect for every occasion, from casual to corporate.',
      cta: 'Explore Collection',
      link: '/shop'
    },
    {
      background: 'https://images.unsplash.com/photo-1599643478518-a784e5dc4c8f?w=1920',
      title: 'Grace',
      subtitle: 'Timeless',
      description: 'Handcrafted pieces that capture the essence of elegance. Make a statement with our exclusive designer jewelry.',
      cta: 'View Designs',
      link: '/shop'
    }
  ];

  // Use carousel slides from DB or fallback to default
  const heroSlides = carouselSlides.length > 0 ? carouselSlides : defaultHeroSlides;

  // Fetch carousel slides from API
  useEffect(() => {
    const fetchCarouselSlides = async () => {
      try {
        const response = await getCarouselSlides();
        const slidesData = response.data?.data;
        if (slidesData && slidesData.length > 0) {
          // Transform API response to match expected format
          const slides = slidesData.map(slide => ({
            background: slide.image_url,
            title: slide.title || '',
            subtitle: slide.subtitle || '',
            description: slide.description || '',
            cta: slide.cta_text || 'Shop Now',
            link: slide.cta_link || '/shop'
          }));
          setCarouselSlides(slides);
        }
      } catch (error) {
        console.error('Error fetching carousel slides:', error);
        // Keep using default slides on error
      }
    };
    fetchCarouselSlides();
  }, []);

  // Auto-advance slides
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [heroSlides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length);
  };

  const testimonials = [
    {
      name: 'Priya Sharma',
      location: 'Mumbai',
      rating: 5,
      text: 'Absolutely stunning pieces! The quality exceeded my expectations. Will definitely order again.',
      image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100'
    },
    {
      name: 'Anjali Patel',
      location: 'Delhi',
      rating: 5,
      text: 'Beautiful jewelry at affordable prices. The packaging was premium and delivery was quick!',
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100'
    },
    {
      name: 'Meera Reddy',
      location: 'Bangalore',
      rating: 5,
      text: 'Love how elegant these pieces look. Everyone asks me where I got them from!',
      image: 'https://images.unsplash.com/photo-1489424731084-a5d8b219a5bb?w=100'
    }
  ];

  const handleNewsletterSubmit = (e) => {
    e.preventDefault();
    if (email) {
      toast.success('Thank you for subscribing! You\'ll receive exclusive offers soon.');
      setEmail('');
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [itemsRes, categoriesRes] = await Promise.all([
          getItems({ limit: 8 }),
          getCategories()
        ]);
        setFeaturedProducts(itemsRes.data.data || []);
        setCategories(categoriesRes.data.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const features = [
    { icon: Sparkle, title: 'Premium Quality', desc: 'Crafted with precision and care' },
    { icon: Truck, title: 'Fast Delivery', desc: 'Pan India shipping available' },
    { icon: ShieldCheck, title: 'Secure Shopping', desc: '100% safe & secure checkout' },
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Carousel */}
      <section className="relative h-[90vh] flex items-center overflow-hidden">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentSlide}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.7 }}
            className="absolute inset-0 bg-cover bg-center"
            style={{ 
              backgroundImage: `url(${heroSlides[currentSlide].background})`,
            }}
          >
            <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
          </motion.div>
        </AnimatePresence>
        
        {/* Navigation Arrows */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all"
          aria-label="Previous slide"
        >
          <CaretLeft size={24} />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all"
          aria-label="Next slide"
        >
          <CaretRight size={24} />
        </button>
        
        {/* Navigation Dots */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentSlide 
                  ? 'bg-primary w-8' 
                  : 'bg-white/50 hover:bg-white/80'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -30 }}
              transition={{ duration: 0.8 }}
              className="max-w-2xl"
            >
              <span className="inline-block font-body text-primary text-sm uppercase tracking-widest mb-4">
                New Collection 2026
              </span>
              <h1 className="font-display text-5xl md:text-7xl text-white leading-tight mb-6">
                {heroSlides[currentSlide].title} <br />
                <span className="italic">{heroSlides[currentSlide].subtitle}</span>
              </h1>
              <p className="font-body text-gray-300 text-lg mb-8 leading-relaxed">
                {heroSlides[currentSlide].description}
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to={heroSlides[currentSlide].link} 
                  data-testid="hero-shop-btn"
                  className="btn-primary flex items-center gap-2"
                >
                  {heroSlides[currentSlide].cta} <ArrowRight size={18} />
                </Link>
                <Link 
                  to="/about" 
                  data-testid="hero-about-btn"
                  className="btn-secondary"
                >
                  Our Story
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 bg-background-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex items-center gap-4 p-6 bg-white"
              >
                <div className="w-14 h-14 bg-primary/10 flex items-center justify-center">
                  <feature.icon size={28} className="text-primary" weight="light" />
                </div>
                <div>
                  <h3 className="font-display text-lg text-secondary">{feature.title}</h3>
                  <p className="font-body text-sm text-text-secondary">{feature.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      {categories.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="font-display text-4xl text-secondary mb-4">Shop by Category</h2>
              <p className="font-body text-text-secondary max-w-xl mx-auto">
                Explore our carefully curated collections
              </p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {categories.slice(0, 4).map((category, index) => (
                <Link
                  key={category.id}
                  to={`/shop/${category.id}`}
                  data-testid={`category-${category.name.toLowerCase()}`}
                  className="group relative h-64 overflow-hidden"
                >
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                    style={{ 
                      backgroundImage: `url(https://images.unsplash.com/photo-${index === 0 ? '1736436789706-005f2218a96d' : index === 1 ? '1584948221378-93841f589fb7' : index === 2 ? '1689397136362-dce64e557fcc' : '1767921777873-81818b812a4d'}?w=400)`,
                    }}
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-all duration-300" />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <h3 className="font-display text-2xl text-white group-hover:text-primary transition-colors duration-300">
                      {category.name}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Products */}
      <section className="py-20 bg-background-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center mb-12 gap-4">
            <div>
              <h2 className="font-display text-4xl text-secondary mb-2">Featured Collection</h2>
              <p className="font-body text-text-secondary">Handpicked pieces just for you</p>
            </div>
            <Link 
              to="/shop" 
              data-testid="view-all-btn"
              className="btn-secondary flex items-center gap-2"
            >
              View All <ArrowRight size={16} />
            </Link>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-gray-200" />
                  <div className="p-4 space-y-2">
                    <div className="h-4 bg-gray-200 rounded" />
                    <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {featuredProducts.slice(0, 8).map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section */}
      {/* <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-display text-4xl text-secondary mb-4">What Our Customers Say</h2>
            <p className="font-body text-text-secondary max-w-xl mx-auto">
              Join thousands of happy customers who trust Luster Adorn
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white p-6 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} weight="fill" className="text-primary" />
                  ))}
                </div>
                <p className="font-body text-text-secondary italic mb-6 leading-relaxed">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-display text-secondary">{testimonial.name}</p>
                    <p className="font-body text-xs text-text-muted">{testimonial.location}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Newsletter Section */}
      {/* <section className="py-16 bg-primary/5">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <EnvelopeSimple size={40} className="text-primary mx-auto mb-4" />
            <h2 className="font-display text-3xl text-secondary mb-3">Stay in the Loop</h2>
            <p className="font-body text-text-secondary mb-8 max-w-lg mx-auto">
              Subscribe to get exclusive offers, new arrivals, and styling tips straight to your inbox.
            </p>
            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                className="flex-1 px-4 py-3 border border-border-default focus:border-primary focus:outline-none font-body"
              />
              <button type="submit" className="btn-primary whitespace-nowrap">
                Subscribe
              </button>
            </form>
            <p className="font-body text-xs text-text-muted mt-4">
              No spam, unsubscribe anytime.
            </p>
          </motion.div>
        </div>
      </section> */}

      {/* Recently Viewed */}
      <RecentlyViewed />

      {/* FAQ Section */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <FAQSection limit={5} />
          <div className="text-center mt-8">
            <Link 
              to="/faq" 
              data-testid="view-all-faq"
              className="btn-secondary inline-flex items-center gap-2"
            >
              View All FAQs <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl md:text-5xl text-white mb-6">
              Ready to <span className="text-primary italic">Shine</span>?
            </h2>
            <p className="font-body text-gray-400 text-lg mb-8 max-w-2xl mx-auto">
              Connect with us on WhatsApp for personalized recommendations, 
              exclusive offers, and quick checkout.
            </p>
            <a 
              href="https://wa.me/919421147643?text=Hi! I'm interested in your jewelry collection."
              target="_blank"
              rel="noopener noreferrer"
              data-testid="whatsapp-cta"
              className="whatsapp-btn inline-flex"
            >
              <WhatsappLogo size={24} weight="fill" />
              <span className="font-body font-medium">Chat with Us</span>
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
